// Play-off bracket: single elimination seeded from the final standings.
//
// Pure. The bracket decides who wins the league, so the pairing and the
// advancement rules are kept away from Firestore and tested directly.

/**
 * Standard bracket seed order for a power-of-two draw.
 *
 * Built by reflection: a draw of 2n takes the order for n and replaces each
 * seed s with the pair (s, 2n+1-s). That is what guarantees the top two seeds
 * can only meet in the final, and seeds 1 and 4 in the semi — the property a
 * naive 1v2, 3v4 pairing loses.
 */
export function seedOrder(size) {
  let order = [1];
  while (order.length < size) {
    const n = order.length * 2;
    const next = [];
    order.forEach(s => { next.push(s, n + 1 - s); });
    order = next;
  }
  return order;
}

/** Smallest power of two that holds `n`. */
export const bracketSize = (n) => {
  let s = 1;
  while (s < n) s *= 2;
  return Math.max(s, 1);
};

/**
 * Build the whole bracket from an ordered seed list.
 *
 * Returns rounds of matches. A seed beyond the field is a bye: the opponent
 * advances without playing, which is how a non-power-of-two field is handled
 * rather than refusing to seed one.
 *
 * Each match carries where its winner goes — nextRound / nextIndex / nextSlot —
 * so advancing a result is a lookup rather than a re-derivation.
 */
export function buildBracket(seeds) {
  const field = (seeds || []).filter(Boolean);
  if (field.length < 2) return { rounds: [], byes: [] };
  const size = bracketSize(field.length);
  const order = seedOrder(size);
  const bySeed = new Map(field.map((s, i) => [i + 1, s]));

  const rounds = [];
  const byes = [];

  // Round 1 from the seed order, two seeds at a time.
  const first = [];
  for (let i = 0; i < order.length; i += 2) {
    const a = bySeed.get(order[i]) || null;      // null = bye
    const b = bySeed.get(order[i + 1]) || null;
    const idx = first.length;
    if (a && !b) byes.push({ round: 0, index: idx, team: a });
    if (b && !a) byes.push({ round: 0, index: idx, team: b });
    first.push({
      round: 0, index: idx,
      homeSeed: order[i], awaySeed: order[i + 1],
      home: a, away: b,
      // A bye resolves immediately — nobody plays a ghost.
      winner: a && !b ? a : (b && !a ? b : null),
    });
  }
  rounds.push(first);

  // Subsequent rounds are empty shells until results arrive.
  let count = first.length;
  while (count > 1) {
    count = Math.floor(count / 2);
    const r = rounds.length;
    rounds.push(Array.from({ length: count }, (_, index) => ({
      round: r, index, homeSeed: null, awaySeed: null,
      home: null, away: null, winner: null,
    })));
  }

  // Wire each match to its parent, and carry byes straight through.
  rounds.forEach((round, r) => {
    round.forEach(m => {
      if (r < rounds.length - 1) {
        m.nextRound = r + 1;
        m.nextIndex = Math.floor(m.index / 2);
        m.nextSlot = m.index % 2 === 0 ? 'home' : 'away';
      }
      if (m.winner && m.nextRound !== undefined) {
        rounds[m.nextRound][m.nextIndex][m.nextSlot] = m.winner;
      }
    });
  });

  return { rounds, byes };
}

/** Label for a round, counting back from the final. */
export function roundName(round, totalRounds) {
  const fromEnd = totalRounds - round;
  if (fromEnd === 1) return 'Final';
  if (fromEnd === 2) return 'Semi-finals';
  if (fromEnd === 3) return 'Quarter-finals';
  return `Round ${round + 1}`;
}

/**
 * Where a finished match sends its winner.
 *
 * Returns null for the final — nothing to advance into, that team has won the
 * league. Callers must treat null as "done", not as an error.
 */
export function advancementTarget(match, totalRounds) {
  if (!match || match.round >= totalRounds - 1) return null;
  return {
    round: match.round + 1,
    index: Math.floor(match.index / 2),
    slot: match.index % 2 === 0 ? 'home' : 'away',
  };
}

/**
 * Group stored play-off matches into rounds, each ordered by index.
 *
 * The admin schedule and the public league page both draw from this, so the
 * two screens cannot end up disagreeing about the shape of the draw.
 */
export function groupRounds(matches) {
  const depth = (matches || []).reduce((n, m) => Math.max(n, (m.round ?? 0) + 1), 0);
  const rounds = Array.from({ length: depth }, () => []);
  (matches || []).forEach(m => { rounds[m.round ?? 0].push(m); });
  rounds.forEach(r => r.sort((a, b) => (a.index ?? 0) - (b.index ?? 0)));
  return { rounds, totalRounds: depth };
}

/**
 * A first-round match with only one team: that side advances unopposed.
 *
 * Worth distinguishing from an empty slot in a later round, which is a real
 * opponent not yet decided — both look like a blank line otherwise.
 */
export const isBye = (m) =>
  (m?.round ?? 0) === 0 && Boolean(m?.homeTeamId) !== Boolean(m?.awayTeamId);

/** Winner of a played match, by score. Null while unplayed or drawn. */
export function winnerOf(match) {
  const h = match?.homeScore, a = match?.awayScore;
  if (h === null || h === undefined || h === '' || a === null || a === undefined || a === '') return null;
  if (Number(h) > Number(a)) return { id: match.homeTeamId, name: match.homeTeamName };
  if (Number(a) > Number(h)) return { id: match.awayTeamId, name: match.awayTeamName };
  return null;   // a draw cannot advance anyone
}
